import { create } from "zustand";
import { axiosHelper, tokenHelper } from "./helpers";
import { useAuth } from "./Auth/Auth";
import { getCookie, setCookie } from "../helpers/cookies";
import { decrypt, encrypt } from "../helpers/sessions";
import { normalizePermissions } from "../helpers/planLimits";

const syncSessionFromInfos = async (infos, accessToken) => {
  const sessionRaw = getCookie("session");
  const payload = await decrypt(sessionRaw);
  if (!payload) return;

  const permissions =
    normalizePermissions(infos.permission) ?? infos.permissions ?? payload.permissions ?? {};

  const expiresAt = new Date("2099-12-31T23:59:59Z");
  const session = await encrypt({
    accessToken,
    expiresAt,
    user: infos,
    permissions,
  });

  setCookie("session", session, {
    expires: expiresAt,
    secure: true,
    sameSite: "lax",
  });

  useAuth.setState({ user: infos, accessToken });
};

export const useUsageStore = create((set, get) => ({
    usage: null,
    permissions: null,
    hasActiveSubscription: null,
    isLoading: false,
    error: null,

    fetchUsage: async () => {
        set({ isLoading: true, error: null });
        try {
            const accessToken = await tokenHelper.getToken()
            const response = await axiosHelper.get("/user/infos", {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const livePermissions =
              normalizePermissions(response.permission) ?? response.permissions ?? null;

            set({
              usage: response.planUsage,
              hasActiveSubscription: response.hasActiveSubscription ?? false,
              permissions: livePermissions,
              isLoading: false,
              role: response.role,
            });

            await syncSessionFromInfos(response, accessToken);

            return response.planUsage;
        } catch (error) {
            set({
              error: error.message,
              isLoading: false,
              hasActiveSubscription: false,
              permissions: null,
            });
            return null;
        }
    },

    /**
     * Check if a specific resource has reached its limit
     * @param {string} resource - The resource name (e.g., 'sites', 'spaces', 'menus')
     * @returns {boolean} - True if allowed (limit not reached), false if limit reached
     */
    canCreate: (resource) => {
        const { usage } = get();
        const user = useAuth.getState().user;

        // ROOT bypass
        if (user?.role === 'ROOT') return true;

        if (!usage) return true;

        // Convert camelCase to snake_case for the limit key (e.g., menuItems -> max_menu_items)
        const snakeResource = resource.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        const limitKey = `max_${snakeResource.toLowerCase()}`;
        const limit = usage.limits[limitKey];

        // Usage key matches the provided resource name (e.g., 'sites', 'menuItems')
        const currentUsage = usage.usage[resource];

        if (limit === undefined || limit === null || limit === -1) return true; // Unlimited or not defined
        return currentUsage < limit;
    },

    /**
     * Get detailed limit info for a resource
     * @param {string} resource - The resource name (e.g., 'sites', 'menuItems')
     */
    getLimitInfo: (resource) => {
        const { usage } = get();
        const user = useAuth.getState().user;

        // ROOT bypass
        if (user?.role === 'ROOT') {
            return {
                limit: 'Unlimited',
                current: 0,
                isReached: false,
                remaining: Infinity,
                isUnlimited: true
            };
        }

        if (!usage) return null;

        const snakeResource = resource.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        const limitKey = `max_${snakeResource.toLowerCase()}`;
        const limit = usage.limits[limitKey];
        const currentUsage = usage.usage[resource];

        const isUnlimited = limit === null || limit === -1;

        return {
            limit: isUnlimited ? 'Unlimited' : limit,
            current: currentUsage,
            isReached: !isUnlimited && currentUsage >= limit,
            remaining: isUnlimited ? Infinity : Math.max(0, limit - currentUsage),
            isUnlimited
        };
    }
}));
